<template>
  <ViewContainer :title="$t('menu.admin.admin_people')">
    <template v-slot:header-right>
      <CreateButton
        small
        testId="people-button-addUsers"
        :disabled="!canAddUsers"
        :tooltipText="$t('common.button.add')"
        @click="handleAddUsersClick"
      />
    </template>
    <template v-if="organizationPlan === 'essentials'">
      <v-sheet rounded elevation="2" class="d-flex pa-4 align-center mb-3">
        <p class="grey--text label ma-0">
          {{
            $t('people.accounts_created_of_accounts_allowed', {
              accountsCreated: currentAccountsCreated,
              accountsAllowed: maximumAllowedAccountsToCreate,
            })
          }}
        </p>
        <v-progress-linear
          rounded
          height="6px"
          :value="currentAccountsCreatedPercentage"
        />
        <span
          class="label px-3 support-cta grey--text"
          data-testid="people-span-talkToSupport"
          role="button"
          tabindex="0"
          @click="openSupportChat"
          @keydown.enter="openSupportChat"
          @keydown.space.prevent="openSupportChat"
        >
          {{ $t('people.talk_to_support_to_upgrade') }}
        </span>
      </v-sheet>
    </template>
    <v-card style="min-height: 600px" class="ma-0 pa-0 px-6">
      <v-card-title class="px-0 mx-0 justify-space-between">
        <div class="d-flex">
          <v-btn
            rounded
            small
            depressed
            class="me-2"
            data-testid="people-button-toggleFilters"
            data-tourid="people-button-toggleFilters"
            :outlined="!showFilters"
            :color="showFilters ? 'primary' : 'blue-grey'"
            @click="showFilters = !showFilters"
          >
            <v-icon left>mdi-tune</v-icon>
            <p class="mr-1 ma-0 pa-0">
              {{
                showFilters
                  ? $t('explore.hide_filters')
                  : $t('explore.show_filters')
              }}
            </p>
            <p class="ma-0 pa-0" v-if="activeFiltersCount">
              ({{ activeFiltersCount }})
            </p>
          </v-btn>
          <Button
            plain
            v-if="activeFiltersCount && showFilters"
            color="primary"
            size="small"
            data-testid="people-button-clearAllFilters"
            icon="mdi-close"
            :text="$t('common.clear_all')"
            :disabled="!hasFiltersSelected"
            @click="clearAllFilters"
          />
        </div>

        <div class="d-flex justify-space-between align-center">
          <v-btn
            outlined
            color="blue-grey lighten-1"
            class="mr-4"
            :loading="isGeneratingReport"
            @click="generateReport"
          >
            <v-icon left color="blue-grey darken-1">save_alt</v-icon>

            <span class="blue-grey--text text--darken-3">{{
              $t('common.button.export_xlsx')
            }}</span>
          </v-btn>

          <SearchInput
            class="me-4"
            :value="searchTerms"
            @change="handleSearch"
          />
        </div>
      </v-card-title>

      <v-card-text class="mx-0 px-0">
        <v-row class="ma-0 pa-0">
          <v-col
            v-show="showFilters"
            cols="12"
            :md="3"
            class="mx-0 px-0 pe-4 mt-3"
          >
            <PeopleFilters
              :organizationId="organizationId"
              :filters="selectedFilters"
              @change="handleFiltersChange"
            />
          </v-col>
          <v-col cols="12" class="px-0" :md="showFilters ? 9 : 12">
            <div
              v-if="searchTerms && !users.length"
              class="d-flex align-center justify-center"
              style="min-height: 600px"
            >
              <EmptyState />
            </div>
            <div v-else>
              <div class="d-flex justify-space-between align-center">
                <p>
                  {{
                    $t('common.resultWithCount_other', {
                      count: totalRecordsInData,
                    })
                  }}
                </p>
                <div class="d-flex justify-end mt-n1 mb-2">
                  <ColumnsSelector
                    :headers="headers"
                    @toggleColumn="onToggleColumn"
                  />
                </div>
              </div>
              <v-card outlined>
                <v-data-table
                  data-private
                  calculate-widths
                  :headers="columns"
                  :items="users"
                  :options.sync="options"
                  :server-items-length="totalRecordsInData"
                  :loading="isLoading"
                  :footer-props="footerOptions"
                >
                  <template v-slot:item.displayname="{ item }">
                    <ChipAvatar
                      link
                      outlined
                      small
                      disablePopover
                      :vuetifyProps="{
                        disabled: item.isDeleted,
                      }"
                      :account="item"
                      @click="goToProfile(item)"
                    />
                  </template>

                  <template v-slot:item.email="{ item }">
                    <span
                      class="text-body-2"
                      :class="{ disabled: item.isDeleted }"
                      >{{ item.email }}</span
                    >
                  </template>

                  <template v-slot:item.status="{ item }">
                    <div class="d-flex align-center">
                      <v-badge
                        dot
                        inline
                        :color="getBadgeColor(item)"
                        :class="{ disabled: item.isDeleted }"
                      />
                      <span
                        class="ms-2"
                        :class="{
                          'red--text': item.isDeleted,
                          disabled: item.isDeleted,
                        }"
                        >{{ item.statusText }}</span
                      >
                    </div>
                  </template>

                  <template v-slot:item.tags="{ item }">
                    <div class="d-flex align-center flex-wrap mb-1">
                      <v-chip
                        outlined
                        small
                        class="me-2 mt-1"
                        v-for="tag in item.tags"
                        :key="tag.id"
                      >
                        <span class="blue-grey--text text--darken-3 caption">{{
                          tag.name
                        }}</span>
                      </v-chip>
                    </div>
                  </template>

                  <template v-slot:item.adminRole="{ item }">
                    <div v-if="item.isDeleted">
                      <v-chip
                        v-if="item.adminRole.id !== 'none'"
                        small
                        outlined
                        class="my-2 me-2 disabled"
                      >
                        <span>{{ item.adminRole.text }}</span>
                      </v-chip>
                    </div>

                    <div v-else data-testid="people-div-adminRole">
                      <v-btn
                        v-if="item.adminRole.id === ADMIN_ROLES.NONE"
                        small
                        text
                        rounded
                        color="blue"
                        data-testid="people-button-assignRole"
                        @click="openRoleAssignmentModal(item)"
                      >
                        <v-icon left size="16" class="my-0 py-0"
                          >mdi-plus-circle-outline
                        </v-icon>
                        {{ $t('common.select') }}
                      </v-btn>
                      <v-chip
                        v-else
                        small
                        outlined
                        data-testid="people-chip-adminRole"
                        @click="openRoleAssignmentModal(item)"
                      >
                        <span>{{ item.adminRole.text }}</span>
                      </v-chip>
                    </div>
                  </template>

                  <template v-slot:item.permissions="{ item }">
                    <div v-if="item.isDeleted">
                      <div class="d-flex align-center flex-wrap mb-1">
                        <v-chip
                          v-for="permission in item.permissions"
                          v-show="permission.value"
                          small
                          outlined
                          class="mt-1 me-2 disabled"
                          :key="permission.id"
                        >
                          <span>{{ permission.text }}</span>
                        </v-chip>
                      </div>
                    </div>

                    <v-edit-dialog
                      v-if="canEdit(item)"
                      @close="setPermission(item)"
                    >
                      <v-btn
                        v-show="!hasPermissions(item.permissions)"
                        small
                        text
                        rounded
                        class="mt-1"
                        color="blue"
                      >
                        <v-icon left size="16" class="my-0 py-0"
                          >mdi-plus-circle-outline
                        </v-icon>
                        {{ $t('common.select') }}
                      </v-btn>
                      <div class="d-flex align-center flex-wrap mb-1">
                        <v-chip
                          small
                          outlined
                          class="mt-1 me-2"
                          v-for="permission in item.permissions"
                          v-show="permission.value"
                          :key="permission.id"
                          :class="{ disabled: item.isDeleted }"
                        >
                          <span>{{ permission.text }}</span>
                          <v-icon
                            class="ms-1"
                            size="small"
                            color="blue-grey"
                            @click.stop="
                              ;(permission.value = false),
                                removePermission(item, permission)
                            "
                            >mdi-close-circle-outline</v-icon
                          >
                        </v-chip>
                      </div>

                      <template v-slot:input>
                        <div
                          v-for="permission in item.permissions"
                          class="menu-item ps-1 pe-3 py-2 mt-1"
                          :key="permission.id"
                        >
                          <v-checkbox
                            hide-details
                            class="ma-0 pa-0"
                            v-model="permission.value"
                            :ripple="false"
                          >
                            <template v-slot:label>
                              <span
                                class="mx-0 px-0 font-size-13 font-weight-500"
                              >
                                {{ permission.text }}
                              </span>
                            </template>
                          </v-checkbox>
                        </div>
                      </template>
                    </v-edit-dialog>

                    <div v-else>
                      <div class="d-flex align-center flex-wrap mb-1">
                        <v-chip
                          v-for="permission in item.permissions"
                          v-show="permission.value"
                          small
                          outlined
                          class="mt-1 me-2"
                          :key="permission.id"
                        >
                          <span>{{ permission.text }}</span>
                        </v-chip>
                      </div>
                    </div>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <div class="text-center">
                      <v-menu offset-y :disabled="!canEdit(item)">
                        <template v-slot:activator="{ on, attrs }">
                          <Button
                            v-bind="attrs"
                            v-on="on"
                            color="grey"
                            size="small"
                            icon="mdi-dots-vertical"
                            :disabled="!canEdit(item)"
                          />
                        </template>
                        <v-list dense>
                          <div
                            v-for="action in item.actions"
                            class="menu-item mx-1 px-4 py-2 mt-1 cursor-pointer"
                            data-testid="people-div-rowActionMenuItem"
                            role="button"
                            tabindex="0"
                            :key="action.type"
                            @click="handleClick(action.type, item)"
                            @keydown.enter="handleClick(action.type, item)"
                          >
                            <v-icon
                              v-text="action.icon"
                              class="me-4"
                              color="blue-grey darken-1"
                            />
                            <span
                              class="mx-0 px-0 font-size-13 font-weight-500"
                              >{{ action.text }}</span
                            >
                          </div>
                        </v-list>
                      </v-menu>
                    </div>
                  </template>
                </v-data-table>
              </v-card>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <ResetPasswordDialog
      v-if="modals.resetPassword"
      :show="modals.resetPassword"
      :email="account.email"
      :isLoading="isReseting"
      @cancel="hideModal('resetPassword')"
      @reset="resetPassword"
    />

    <AddUserModalBulk
      size="lg"
      :canAssignRole="canAssignRole"
      :canInviteExternalUsers="canInviteExternalUsers"
      :organizationId="organizationId"
      :remainingUsersToAdd="remainingUsersToAdd"
      :rolesEnabled="rolesEnabled"
      :show="modals.addUsers"
      @closeModal="hideModal('addUsers')"
      @onUsersAdded="onUsersAdded"
    />

    <EditAccountDialog
      v-if="modals.editAccount && !isAccountEmpty"
      :show="modals.editAccount"
      :account="account"
      :organizationId="organizationId"
      @update="onEditAccountDialogSubmit"
      @cancel="onCancelAttributesModal"
    />

    <DeleteConfirmationModal
      :show="modals.deleteUser"
      :isLoading="isDeleting"
      :title="$t('common.users.remove_this_account')"
      :text="$t('common.users.delete_account_text')"
      :confirmText="$t('common.button.deactivate')"
      @cancel="hideModal('deleteUser')"
      @delete="deleteAccount"
    />

    <RoleAssignmentModal
      :show="modals.roleAssignment"
      :user="account"
      @cancel="hideModal('roleAssignment')"
      @saved="saveRoleAssignment"
      @error="handleRoleAssignmentError"
    />

    <ReportsDrawer
      v-if="isReportsDrawerOpen"
      :visible="isReportsDrawerOpen"
      :organizationId="organizationId"
      :isReportInfoAlertShown="isReportInfoAlertShown"
      @close="isReportsDrawerOpen = false"
      @closeAlert="isReportInfoAlertShown = false"
    />

    <PeopleExportableDialog
      :show="isSiteAdminAlertShown"
      @export="handleExportAsSiteAdmin"
      @cancel="isSiteAdminAlertShown = false"
    />
  </ViewContainer>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import access from '@/lib/access.js'
import api from '@/api'
import adminRoleMixin from '@/mixins/adminRoleMixin'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import PeopleFilters from './PeopleFilters.vue'
import PeopleExportableDialog from './PeopleExportableDialog.vue'
import AddUserModalBulk from './AddPeopleModal/AddUserModalBulk.vue'
import EditAccountDialog from './EditAccountDialog.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import ColumnsSelector from '@/components/ColumnsSelector.vue'
import ResetPasswordDialog from '@/components/People/ResetPasswordDialog.vue'
import ReportsDrawer from '@/components/ReportsDrawer/ReportsDrawer.vue'
import RoleAssignmentModal from '@/components/Admin/Organizations/RoleAssignmentModal.vue'

import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import Button from '@/components/UI/Button'
import CreateButton from '@/components/UI/CreateButton'
import SearchInput from '@/components/Core/SearchInput.vue'
import EmptyState from '@/components/People/EmptyState.vue'

const SORT_BY_FIELD = 'updatedTimestamp'
const DEFAULT_SIZE = 20
const SIMILAR_REPORT_ERROR_CODE = 'SRE001'
const EXPORTABLE_TYPE = 'people_list'
const SORT_BY_COINCIDENCE = '_score'

export default {
  name: 'AdminPeople',

  mixins: [mixin, trackerMixin, adminRoleMixin],

  components: {
    EditAccountDialog,
    ViewContainer,
    PeopleFilters,
    DeleteConfirmationModal,
    Button,
    CreateButton,
    SearchInput,
    EmptyState,
    ChipAvatar,
    ColumnsSelector,
    AddUserModalBulk,
    ResetPasswordDialog,
    PeopleExportableDialog,
    ReportsDrawer,
    RoleAssignmentModal,
  },

  data() {
    return {
      isReseting: false,
      isReportsDrawerOpen: false,
      isReportInfoAlertShown: false,
      isSiteAdminAlertShown: false,
      isGeneratingReport: false,
      users: [],
      modals: {
        addUsers: false,
        deleteUser: false,
        editAccount: false,
        resetPassword: false,
        roleAssignment: false,
      },
      account: {},
      isLoading: false,
      showFilters: true,
      totalRecordsInData: 0,
      searchTerms: '',
      currentAccountsCreated: 0,
      currentPage: 1,
      currentItemsPerPage: DEFAULT_SIZE,
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      isDeleting: false,
      selectedFilters: {
        countryCodes: [],
        sites: [],
        status: '',
        permission: '',
        role: '',
        siteGroups: [],
        tags: [],
        tagGroups: [],
      },
      headers: [
        {
          text: this.$t('common.name'),
          align: 'start',
          sortable: true,
          value: 'displayname',
          show: true,
        },
        {
          text: this.$t('common.email_one'),
          sortable: false,
          value: 'email',
          show: true,
        },
        {
          text: this.$t('common.status'),
          sortable: true,
          value: 'status',
          show: true,
        },
      ],
    }
  },

  async mounted() {
    this.buildHeaders()
  },

  watch: {
    options: {
      deep: true,
      immediate: false,
      handler(newVal, oldVal) {
        if (newVal.page !== oldVal.page) {
          this.trackPeopleNavigate({
            section: 'table',
            element: 'paginator',
            page: newVal.page,
          })
        }
      },
    },
  },

  computed: {
    ...mapGetters([
      'accountId',
      'authorizedSiteIds',
      'organization',
      'organizationId',
    ]),

    ...mapState({
      user: state => state.access.account,

      activeFiltersCount() {
        return Object.keys(this.selectedFilters)?.filter(
          filter =>
            !_.isEmpty(this.selectedFilters?.[filter]) ||
            this.selectedFilters?.[filter] === true
        )?.length
      },

      isAccountEmpty() {
        return _.isEmpty(this.account)
      },
    }),

    canInviteExternalUsers() {
      return Boolean(access.isLeadership(this.user))
    },

    hasFiltersSelected() {
      return Object.keys(this.selectedFilters).some(
        key => !_.isEmpty(this.selectedFilters[key])
      )
    },

    canExportSiteAdmin() {
      if (this.user.isOrganizationAdmin || this.user.isAdmin) return true
      return Boolean(
        this.selectedFilters.sites?.length === 1 &&
          this.selectedFilters.sites?.includes?.(this.user.site._id) &&
          this.selectedFilters.siteGroups?.length === 0
      )
    },

    organizationAuthIsLocal() {
      return this.organization.authenticationType.includes('local')
    },

    columns() {
      return this.headers.filter(h => h.show)
    },

    options: {
      get() {
        return {
          itemsLength: this.totalRecordsInData,
          itemsPerPage: this.currentItemsPerPage,
          multiSort: false,
          mustSort: true,
          page: this.currentPage,
        }
      },
      set(event) {
        this.currentPage = event.page
        this.currentItemsPerPage = event.itemsPerPage
        this.getUsers({
          page: event.page,
          itemsPerPage: event.itemsPerPage,
          sortDesc: event.sortDesc[0],
          sortBy: event.sortBy[0],
        })
      },
    },

    canAddUsers() {
      return (
        access.isLeadershipOrCoach(this.getAccount()) &&
        this.currentAccountsCreated < this.maximumAllowedAccountsToCreate
      )
    },

    remainingUsersToAdd() {
      return this.maximumAllowedAccountsToCreate - this.currentAccountsCreated
    },

    organizationPlan() {
      const { plan } = this.$store.getters.organization
      return plan
    },

    currentAccountsCreatedPercentage() {
      return (
        (this.currentAccountsCreated * 100) /
        this.maximumAllowedAccountsToCreate
      )
    },

    maximumAllowedAccountsToCreate() {
      return this.$store.getters.organization.licenses
    },

    rolesEnabled() {
      if (access.isOrganizationAdmin(this.user)) {
        return [
          'isOrganizationAdmin',
          'isSiteAdmin',
          'isCoach',
          'canDoFinancialValidation',
          'isRoutineManager',
        ]
      }
      if (access.isLeadership(this.user)) {
        return ['isCoach', 'canDoFinancialValidation', 'isRoutineManager']
      }
      return []
    },
    canAssignRole() {
      return access.isLeadership(this.user)
    },

    sanitizedFilters() {
      return Object.fromEntries(
        Object.entries(this.selectedFilters).filter(value => {
          return _.isBoolean(value[1]) || value[1].length !== 0
        })
      )
    },
  },

  methods: {
    ...mapActions({
      getAccounts: 'getAccounts',
      createAccount: 'createAccount',
      setSearchSortOrder: 'setSearchSortOrder',
      setSearchSortBy: 'setSearchSortBy',
      updateAccount: 'updateAccount',
      getMe: 'getMe',
    }),

    showModal(modal) {
      this.modals[modal] = true
    },

    hideModal(modal) {
      this.modals[modal] = false
    },

    openRoleAssignmentModal(user) {
      this.account = user
      this.showModal('roleAssignment')
    },

    updateUserInList(user, data) {
      const userId = utils.getEntityId(user)
      const index = this.users.findIndex(
        currentUser => utils.getEntityId(currentUser) === userId
      )
      if (index === -1) return
      this.users.splice(index, 1, {
        ...user,
        ...data,
        adminRole: this.getAdminRole(data),
      })
    },

    saveRoleAssignment({ user, data }) {
      this.trackPeopleNavigate({ section: 'setAdminRole', element: data })
      this.updateUserInList(user, data)
      this.hideModal('roleAssignment')
      this.showSuccess(this.$t('people.admin_role_updated_successfully'))
    },

    handleRoleAssignmentError() {
      this.showError(this.$t('people.admin_role_update_failed'))
    },

    clearAllFilters() {
      Object.keys(this.selectedFilters)?.map(
        key => (this.selectedFilters[key] = [])
      )
      this.handleFiltersChange(this.selectedFilters)
    },

    buildHeaders() {
      if (access.isOrganizationAdmin(this.user)) {
        this.headers.push({
          text: this.$t('common.admin_role_one'),
          sortable: false,
          value: 'adminRole',
          show: true,
        })
      }
      if (access.isLeadership(this.user)) {
        this.headers.push({
          text: this.$t('organization_users.permission_other'),
          sortable: false,
          value: 'permissions',
          show: true,
        })

        this.headers.push({
          text: this.$t('common.tag_other'),
          sortable: false,
          value: 'tags',
          show: false,
          width: 250,
        })
        this.headers.push({
          text: this.$t('common.action_other'),
          sortable: false,
          value: 'actions',
          align: 'center',
          show: true,
        })
      }
    },

    getBadgeColor(item) {
      if (item.isDeleted) return 'red'
      switch (item.status) {
        case 'joined':
          return 'green lighten-1'
        case 'pending':
          return 'yellow lighten-1'
        case 'created':
          return 'blue-grey lighten-1'
        default:
          return ''
      }
    },

    onToggleColumn({ index, value }) {
      this.headers[index].show = value
      this.trackPeopleNavigate({
        element: 'columnSelector',
        section: 'table',
        columns: this.columns.map(col => col.value),
      })
    },

    async handleFiltersChange(filters) {
      this.selectedFilters = { ...filters }
      this.currentPage = 1
      this.trackPeopleFilter({
        section: 'filters',
        filters: this.selectedFilters,
      })
      this.getUsers(this.options)
    },

    handleSearch(terms) {
      this.searchTerms = terms
      this.currentPage = 1

      this.getUsers(this.options)
    },

    async getUsers(options) {
      this.isLoading = true
      const size = options.itemsPerPage || DEFAULT_SIZE
      const terms = this.searchTerms?.toLowerCase() ?? ''

      const sort = options?.sortBy
        ? { by: options.sortBy, order: options.sortDesc ? 'desc' : 'asc' }
        : { by: terms ? SORT_BY_COINCIDENCE : SORT_BY_FIELD, order: 'desc' }

      try {
        const response = await api.account.getAccounts(this.organizationId, {
          limit: options.itemsPerPage || DEFAULT_SIZE,
          from: options.page * size - size,
          terms: this.searchTerms,
          filters: {
            isDeleted: this.searchTerms?.length ? null : false,
            ...this.selectedFilters,
          },
          sort,
          relations: ['tags'],
        })

        this.users = this.buildUsers(response?.data?.results ?? [])

        this.totalRecordsInData = response?.data?.totalRecordsInData ?? 0
        this.currentAccountsCreated = this.totalRecordsInData
        if (this.searchTerms) {
          this.trackPeopleSearch({
            keyword: this.searchTerms,
            isEmpty: !this.totalRecordsInData,
          })
        }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    buildUsers(users) {
      return users.map(user => {
        const permissions = [
          {
            value: user.isCoach,
            text: this.$t('common.roles.coach_one'),
            id: 'isCoach',
          },
          {
            value: user.canDoFinancialValidation,
            text: this.$t('common.roles.impact_validator'),
            id: 'canDoFinancialValidation',
          },
          {
            value: user.isRoutineManager,
            text: this.$t('common.roles.routine_manager'),
            id: 'isRoutineManager',
          },
        ]
        return {
          ...user,
          statusText: this.$t(`people.status.${user.status}`),
          name: this.getFullName(user),
          adminRole: this.getAdminRole(user),
          actions: user.isDeleted
            ? [
                {
                  type: 'reactivate',
                  text: this.$t('people.actions.reactivate'),
                  icon: 'mdi-account-reactivate-outline',
                },
              ]
            : _.compact([
                {
                  type: 'edit',
                  text: this.$t('people.actions.edit'),
                  icon: 'mdi-pencil-outline',
                },
                this.organizationAuthIsLocal && {
                  type: 'reset',
                  text: this.$t('people.actions.reset'),
                  icon: 'mdi-lock-reset',
                },
                {
                  type: 'delete',
                  text: this.$t('people.actions.delete'),
                  icon: 'mdi-account-cancel-outline',
                },
              ]),
          permissions,
        }
      })
    },

    async getTotalAccountsInOrganization() {
      const options = {
        organizationId: this.organizationId,
        sort: {
          by: SORT_BY_FIELD,
          order: 'desc',
        },
        filters: {
          isDeleted: false,
        },
      }

      const results = await this.getAccounts(options)
      this.currentAccountsCreated = results.totalRecordsInData
    },

    onUsersAdded() {
      setTimeout(async () => {
        this.isLoading = true
        await this.getUsers(this.options)
        await this.getTotalAccountsInOrganization()
      }, 500)
    },

    onCancelAttributesModal() {
      this.modals.editAccount = false
      this.account = {}
    },

    async onEditAccountDialogSubmit(account) {
      if (this.isAccountLocationOutOfScope(account)) {
        this.showError(this.$t('validation.user.site_not_authorized'))
        return
      }

      try {
        const accountToUpdate = { ...account, ...account.location }

        await api.account.update(
          account?.organization?._id ?? account?.organization,
          account?._id,
          accountToUpdate
        )
        if (account._id === this.accountId) await this.getMe()

        this.modals.editAccount = false
      } catch (err) {
        this.showError(err.response.data.message)
      } finally {
        await this.getUsers(this.options)
      }
    },

    isAccountLocationOutOfScope(account) {
      if (this.user.isAdmin || this.user.isOrganizationAdmin) {
        return false
      }

      if (!this.user.isSiteAdmin) {
        return true
      }

      return !this.authorizedSiteIds.includes(
        utils.getEntityId(account?.location?.site)
      )
    },

    async setPermission(user) {
      const data = {}
      user.permissions.forEach(permission => {
        data[permission.id] = permission.value
      })

      this.trackPeopleNavigate({ section: 'setPermission', element: data })

      await this.updateAccount({
        account: user,
        data,
      })
    },

    async removePermission(user, permissionToRemove) {
      await this.updateAccount({
        account: user,
        data: { [permissionToRemove.id]: false },
      })
    },

    async resetPassword(password) {
      try {
        this.isReseting = true
        await api.account.resetPassword({
          organizationId: this.organization._id,
          userId: this.account._id,
          password,
        })

        this.showSuccess(this.$t('people.password_successfully_reset'))
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isReseting = false
        this.modals.resetPassword = false
      }
    },

    async deleteAccount() {
      try {
        this.isDeleting = true
        await this.updateAccount({
          account: this.account,
          data: {
            isDeleted: true,
          },
        })
        this.showSuccess(this.$t('people.account_successfully_deleted'))
        await this.getUsers(this.options)
        this.modals.deleteUser = false
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isDeleting = false
      }
    },

    openSupportChat() {
      this.$openSupportChat(this.$t('people.i_would_like_to_upgrade'))
    },

    hasPermissions(permissions) {
      const perm = permissions.filter(p => p.value === true)
      return Boolean(perm.length)
    },

    handleClick(type, user) {
      this.account = user
      this.trackPeopleNavigate({ section: 'tableActions', element: type })
      switch (type) {
        case 'edit':
          this.modals.editAccount = true
          break
        case 'reset':
          this.modals.resetPassword = true
          break
        case 'delete':
          this.modals.deleteUser = true
          break
        case 'reactivate':
          this.reactivateAccount(user)
          break
      }
    },

    async reactivateAccount(user) {
      try {
        await this.createAccount({
          organizationId: this.organizationId,
          account: {
            email: user.email,
            site: user.site,
          },
        })
        this.showSuccess(this.$t('people.account_successfully_activated'))
        await this.getUsers(this.options)
      } catch (error) {
        this.handleRequestError(error)
      }
    },

    canEdit(account) {
      if (this.user.isAdmin || this.user.isOrganizationAdmin) {
        return true
      }

      if (
        this.user.isSiteAdmin &&
        (account.isOrganizationAdmin || account.isAdmin)
      ) {
        return false
      }

      if (!this.user.isSiteAdmin) {
        return false
      }

      return this.authorizedSiteIds.includes(utils.getEntityId(account?.site))
    },

    goToProfile(user) {
      this.trackPeopleNavigate({
        element: 'userChip',
        section: 'tableRow',
      })
      this.navigateTo({
        name: 'profile',
        params: { organizationId: user.organization._id, userId: user._id },
      })
    },

    handleAddUsersClick() {
      this.trackPeopleNavigate({ element: 'addUsersButton', section: 'header' })
      this.modals.addUsers = true
    },

    handleExportAsSiteAdmin() {
      this.selectedFilters.sites = [this.user.site._id]
      this.selectedFilters.siteGroups = []
      this.selectedFilters = { ...this.selectedFilters }
      this.isSiteAdminAlertShown = false
      this.getUsers(this.options)
      this.generateReport()
    },

    async generateReport() {
      try {
        this.isGeneratingReport = true

        if (!this.canExportSiteAdmin) return (this.isSiteAdminAlertShown = true)

        await api.report.generateReport({
          type: EXPORTABLE_TYPE,
          organizationId: this.organizationId,
          filters: this.sanitizedFilters,
          language: this.$store.getters.language ?? 'en',
          locale: window.navigator.language ?? 'en',
        })

        this.isReportsDrawerOpen = true
        this.isReportInfoAlertShown = true
      } catch (err) {
        if (err.response.data.errorCode === SIMILAR_REPORT_ERROR_CODE) {
          return this.showWarning(
            this.$t('insights.report_with_similar_criteria')
          )
        }
        return this.handleRequestError(err)
      } finally {
        this.isGeneratingReport = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.disabled {
  opacity: 0.3;
}

.menu-item {
  border-radius: 4px;
  &:first-child {
    margin-top: 0 !important;
  }
  &:hover {
    background-color: $color-blue-grey-lighten3;
  }
}

.support-cta:hover {
  color: $color-blue !important;
  text-decoration: underline;
  cursor: pointer;
}
</style>

<style lang="scss">
.v-small-dialog__content {
  padding: 4px;
}
</style>
